import {Instance, flow, types as t} from 'mobx-state-tree'
import {Permission, PermissionCondition, satisfyCondition} from 'admin/permissions'
import {union} from 'lodash-es'
import {injectFromEnv, injectFromRoot} from './injectors'
import {ApiError} from 'common/lib/errors'
import CenterRobotApi from 'common/types/api/center-robot'
import config from 'common/config'

const User = t.compose(
  injectFromRoot('roleManager'),
  injectFromEnv('api')
)
.named('User')
.props({
  profile: t.maybeNull(t.model({
    id: t.string,
    firstName: t.string,
    lastName: t.string,
    login: t.string,
    loginLd: t.string,
    position: t.string,
    workEmail: t.string,
    workPhone: t.string
  })),
  /**
   * Роли текущего пользователя
   */
  roles: t.array(t.string)
})
.views((self) => {
  const views = {
    /**
     * Все доступные права пользователя
     */
    get permissions () {
      return self.roles
        .map((role) => self.$root.roleManager.roles.get(role))
        .filter(Boolean)
        .reduce<Permission[]>(
          (result, role) => union(role.permissions, result),
          []
        )
    },

    /**
     * Получить аватарку
     */
    get avatar () {
      if (!self.profile) {
        return null
      }

      return `${config.api.centerBaseURL}${config.api.centerPrefix}/user/${self.profile.login}/avatar/64.jpg`
    },

    /**
     * Удовлетворяют ли права пользователя условиям
     */
    satisfyPermissions (condition: PermissionCondition) {
      return satisfyCondition(condition, views.permissions)
    },

    /**
     * Возвращает роли, которые необходимо получить пользователю, чтобы удовлетворять условиям
     * Нужно получить любую роль из возвращаемого списка, чтобы получить доступ
     */
    getRolesByPermissions (condition: PermissionCondition) {
      if (views.satisfyPermissions(condition)) {
        return []
      }

      return self.$root.roleManager.getRolesByPermissions(condition)
    }
  }

  return views
})
.actions((self) => {
  const actions = {
    fetchProfile: flow(function * () {
      try {
        const response: CenterRobotApi['/user.json']['GET']['response'] =
          yield self.$env.api.centerRobot.get('/user.json')
        self.profile = response.data
      } catch (e) {
        if (!(e instanceof ApiError)) {
          throw e
        }
      }
    })
  }

  return actions
})

export default User
export type UserType = Instance<typeof User>
